import { useState } from 'react'
import { HiX, HiLightBulb, HiExclamation, HiChat, HiDotsHorizontal } from 'react-icons/hi'
import { feedbackApi } from '../../api/client'

const CATEGORIES = [
  { value: 'bug', label: 'Bug Report', icon: HiExclamation, color: 'text-red-500' },
  { value: 'feature', label: 'Feature Request', icon: HiLightBulb, color: 'text-amber-500' },
  { value: 'general', label: 'General', icon: HiChat, color: 'text-blue-500' },
  { value: 'other', label: 'Other', icon: HiDotsHorizontal, color: 'text-surface-500' },
]

export default function FeedbackModal({ onClose }) {
  const [category, setCategory] = useState('general')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!message.trim()) return

    setSubmitting(true)
    try {
      await feedbackApi.submit({ category, message: message.trim() })
      setSubmitted(true)
      setTimeout(() => onClose(), 1800)
    } catch (err) {
      console.error('Failed to submit feedback:', err)
      alert('Failed to submit feedback. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 animate-fade-in" onClick={onClose}>
      <div
        className="bg-white dark:bg-surface-800 rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
          <h2 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
            Send Feedback
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg transition-colors"
          >
            <HiX className="w-5 h-5 text-surface-500" />
          </button>
        </div>

        {submitted ? (
          <div className="px-6 py-12 text-center">
            <div className="w-14 h-14 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200 mb-1">
              Thank you!
            </h3>
            <p className="text-surface-500 dark:text-surface-400">
              Your feedback has been submitted successfully.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
            {/* Category selector */}
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                Category
              </label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map((cat) => {
                  const Icon = cat.icon
                  const selected = category === cat.value
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setCategory(cat.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                        selected
                          ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300 dark:border-brand-600'
                          : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400 hover:border-surface-300 dark:hover:border-surface-600'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${selected ? 'text-brand-500' : cat.color}`} />
                      {cat.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                Your feedback
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what's on your mind..."
                rows={5}
                maxLength={5000}
                className="input w-full resize-none"
                autoFocus
                required
              />
              <p className="text-xs text-surface-400 mt-1 text-right">{message.length}/5000</p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!message.trim() || submitting}
                className="btn btn-primary"
              >
                {submitting ? 'Sending...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
